'use client';

import React, { useState, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import Button from '@/ui/components/shared/Button';
import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import ReactPlayer from 'react-player';
import Image from 'next/image';
import clsx from 'clsx';
import { Option } from '../../../shared/multistepFormComponents/types';

export type ICSelectWithImagesProps = {
  question: string;
  description?: string;
  mainImage?: string;
  extraInfo?: string;
  extraInfoButton?: string;
  underImageText?: string;
  mainClip?: string;
  options: Option[];
  onSelect: (value: any) => void;
};

function ICSelectWithImages({
  description,
  mainImage,
  extraInfo,
  extraInfoButton,
  mainClip,
  options,
  onSelect,
  underImageText,
}: Readonly<ICSelectWithImagesProps>) {
  const [openExtraInfo, setOpenExtraInfo] = useState<boolean>(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoLoaded, setVideoLoaded] = useState<boolean>(false);

  const toggleExtraInfo = () => {
    setOpenExtraInfo((prev) => !prev);
  };

  useEffect(() => {
    if (mainClip) {
      setVideoUrl(mainClip);
    }

    return () => {
      setVideoUrl(null);
    };
  }, [mainClip]);

  const isOneImageVideoMultiChoice = (mainClip || mainImage) && options.length > 1;

  return (
    <div
      className={twMerge(
        'mx-auto w-full max-w-lg flex-grow justify-center text-center',
        (mainImage || mainClip) && 'max-w-xl',
      )}
    >
      {description && (
        <div className="flex w-full justify-center">
          <p className="mb-8 max-w-lg text-center text-md text-gray-500">{description}</p>
        </div>
      )}

      {extraInfo && extraInfoButton && !openExtraInfo && (
        <button
          type="button"
          className="mx-auto mb-6 flex items-center gap-3"
          onClick={(e) => {
            toggleExtraInfo();
            e.stopPropagation();
          }}
        >
          <div className="rounded-full bg-primary-20">
            <InformationCircleIcon className="-m-1 h-6 w-6 text-primary-100" />
          </div>
          <p className="underline">{extraInfoButton}</p>
        </button>
      )}

      {openExtraInfo && (
        <div className="relative mb-2 rounded-lg bg-primary-20 px-4 py-3 pr-6 text-start">
          <button
            type="button"
            className="absolute right-1 top-1"
            onClick={() => toggleExtraInfo()}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
          <p>{extraInfo}</p>
        </div>
      )}

      {/* Main container actually */}
      <div className="mx-auto w-full max-w-lg">
        {mainImage && (
          <Image
            src={mainImage}
            alt="Main"
            className={clsx(
              'mx-auto h-auto max-h-[calc(100svh-26rem)] w-full rounded-t-lg border border-neutral-100 object-contain md:max-h-none',
              {
                'border-b-0': underImageText,
              },
            )}
            width={600}
            height={400}
            sizes="(max-width: 1024px) 100vw, 50vw"
          />
        )}

        {videoUrl && (
          <div
            className={clsx('overflow-hidden rounded-t-lg border border-neutral-100', {
              'border-b-0': underImageText,
            })}
            style={{ aspectRatio: '16 / 9' }}
          >
            <div
              className={twMerge(
                'h-full w-full transition-opacity',
                videoLoaded ? 'opacity-100' : 'opacity-0',
              )}
              style={{
                transition: 'opacity 2s ease-in-out',
              }}
            >
              <ReactPlayer
                url={videoUrl}
                muted
                playing
                loop
                height="100%"
                width="100%"
                className="mx-auto"
                playsinline
                config={{
                  file: {
                    attributes: {
                      crossOrigin: 'true',
                    },
                  },
                }}
                onReady={() => setVideoLoaded(true)} // Trigger fade-in when video is ready
              />
            </div>
          </div>
        )}

        {underImageText && (
          <p className="border border-t-0 border-neutral-100 px-2 pb-3 pt-4 text-left text-gray-500">
            {underImageText}
          </p>
        )}

        <div
          className={clsx('grid auto-rows-auto grid-cols-2 justify-center', {
            'gap-3 lg:gap-5': !isOneImageVideoMultiChoice,
          })}
        >
          {options.map((option, index) => (
            <div
              key={index}
              className={twMerge(
                'col-span-1 flex flex-col items-center',
                options.length % 2 !== 0 &&
                  index === options.length - 1 &&
                  'col-span-2 w-1/2 justify-self-center lg:w-auto', // Center last item if odd
              )}
            >
              {option.image && (
                <button
                  data-id={option.value}
                  type="button"
                  aria-label={option.text}
                  className="w-full rounded-md rounded-b-none border border-neutral-100"
                  onClick={() => onSelect(option.value)}
                >
                  <Image
                    src={option.image}
                    alt={option.text}
                    className="rounded-md"
                    width={500}
                    height={400}
                    objectFit="cover"
                  />

                  {option.underImageText && (
                    <p className="mt-2 px-4 py-3 text-left text-sm text-neutral-50">
                      {option.underImageText}
                    </p>
                  )}
                </button>
              )}

              <div className="mx-auto w-full flex-grow">
                <Button
                  variant="yellowBlackBorder"
                  className={clsx('flex w-full', {
                    'h-full': option.image || isOneImageVideoMultiChoice,
                    'rounded-t-none border-t-0': isOneImageVideoMultiChoice || option.image,
                    'rounded-br-none': index === 0 && isOneImageVideoMultiChoice, // first option of the image
                    'rounded-br-none border-l-0':
                      index > 0 && index > options.length - 1 && isOneImageVideoMultiChoice, // not option in the middle
                    'rounded-bl-none border-l-0':
                      index === options.length - 1 && isOneImageVideoMultiChoice, // last option of the image
                  })}
                  onClick={(e) => {
                    onSelect(option.value);
                    const target = e?.target as HTMLButtonElement;
                    target?.blur(); // Remove focus after click
                  }}
                >
                  {option.text}
                </Button>

                {option.extraInfo && (
                  <p className="mt-2 text-sm text-neutral-50">{option.extraInfo}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ICSelectWithImages;
